import * as openpgp from "openpgp";
import { readFileFromBase64 } from "./readFileFromBase64";
import { getContentKeys } from "./contentKeys";

// F3, domknięcie (docs/model-kluczy-firmowych.md, rozstrzygnięcie 5 sekcji 4): notatki
// zapisane PO db-migrations/2026-09-08_claude_f3_02_notes_encryption_key.sql mają
// Notes.EncryptionKeyId -> ObjectKeys — Title/Content są zaszyfrowane kluczem OBIEKTU,
// a klucz obiektu jest owinięty kluczem FIRMY (encryptNoteFields.ts). Odszyfrowanie idzie
// więc DWUSTOPNIOWO: najpierw klucz obiektu (kluczami slotu 'company' — unia klucza firmy
// i starych kluczy profili, patrz companyContentKeys.ts), dopiero potem nim samą treść.
//
// Notatki sprzed tej migracji mają EncryptionKeyId IS NULL — treść jest tam zaszyfrowana
// WPROST kluczem profilu (stara ścieżka). `legacyPrivateKeys` niesie te klucze od wołającego
// (getProfilePrivateKeys() / getActiveProfilePrivateKeys(), zależnie od miejsca), bo to ONE
// różnią się między NotesDesk/deskModule/NotesModal, a nie sposób deszyfracji.
//
// Był TRZY RAZY zbliżony kod dekodujący notatkę profilem (NotesDesk, deskModule) plus
// czwarta odmiana przez decryptPackage (KSeFInvoiceRegister/NotesModal) — ten util jest
// wspólnym punktem dla wszystkich czterech.

export interface INoteObjectKeyMaterial {
  Id?: string;
  /** Klucz PRYWATNY obiektu notatki, zaszyfrowany kluczem firmy — base64 binarnego PGP. */
  PrivateKey?: string | null;
}

export interface INoteContentSource {
  Title?: string | null;
  Content?: string | null;
  EncryptionKeyId?: string | null;
  ObjectKeys?: INoteObjectKeyMaterial[] | null;
}

/** Wybiera materiał klucza obiektu notatki z odpowiedzi BE (lista ma 0 albo 1 element). */
const pickNoteObjectKey = (
  note: INoteContentSource,
): INoteObjectKeyMaterial | null => {
  if (!note.EncryptionKeyId) return null;
  const keys = note.ObjectKeys ?? [];
  return keys.find((k) => k.Id === note.EncryptionKeyId) ?? keys[0] ?? null;
};

/** Rozpakowuje klucz PRYWATNY obiektu notatki kluczami firmy (unia z legacy w slocie 'company'). */
const unwrapNoteObjectKey = async (
  objectKey: INoteObjectKeyMaterial,
): Promise<openpgp.key.Key[]> => {
  if (!objectKey.PrivateKey) {
    throw new Error("Klucz obiektu notatki bez materiału prywatnego");
  }

  const companyKeys = getContentKeys("company");
  if (!companyKeys.length) {
    throw new Error(
      "Brak klucza firmy do odszyfrowania klucza obiektu notatki",
    );
  }

  const wrappedData = await readFileFromBase64(
    objectKey.PrivateKey,
    "application/other",
  );
  const wrappedMessage = await openpgp.message.read(
    new Uint8Array(wrappedData),
  );
  const decryptedKey = await openpgp.decrypt({
    message: wrappedMessage,
    privateKeys: companyKeys,
    format: "binary",
  });

  const objectPrivateKey = await openpgp.key.read(decryptedKey.data);
  if (!objectPrivateKey.keys.length) {
    throw new Error("Nie udało się odczytać klucza obiektu notatki");
  }

  return objectPrivateKey.keys;
};

/** Odszyfrowuje JEDEN fragment notatki (Title albo Content) danym zestawem kluczy. */
const decryptWithKeys = async (
  msg: string,
  privateKeys: openpgp.key.Key[],
): Promise<string> => {
  const data = await readFileFromBase64(msg, "application/other");
  const message = await openpgp.message.read(new Uint8Array(data));
  const decrypted = await openpgp.decrypt({
    message,
    privateKeys,
    format: "binary",
  });

  return new TextDecoder().decode(decrypted.data);
};

/**
 * Odszyfrowuje Title i Content JEDNEJ notatki. Wybiera ścieżkę automatycznie po
 * `EncryptionKeyId`: obiektową (nowe notatki) albo starą wprost-profilową (notatki sprzed
 * F3, `legacyPrivateKeys`). Pusty `legacyPrivateKeys` jest legalny dla notatek z kluczem
 * obiektu — parametr jest wtedy po prostu nieużywany.
 */
export const decryptNoteFields = async (
  note: INoteContentSource,
  legacyPrivateKeys: openpgp.key.Key[],
): Promise<{ title: string; content: string }> => {
  const objectKey = pickNoteObjectKey(note);
  const privateKeys = objectKey
    ? await unwrapNoteObjectKey(objectKey)
    : legacyPrivateKeys;

  if (!privateKeys.length) {
    throw new Error("Brak klucza do odszyfrowania notatki");
  }

  const [title, content] = await Promise.all([
    note.Title ? decryptWithKeys(note.Title, privateKeys) : "",
    note.Content ? decryptWithKeys(note.Content, privateKeys) : "",
  ]);

  return { title, content };
};

export default decryptNoteFields;
